import { useState, useCallback, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import {
  Loader2,
  ArrowLeft,
  ArrowRight,
  Check,
  Sparkles,
  AlertTriangle,
  FileSearch,
  Settings2,
  Eye,
  LayoutPanelLeft,
  List,
  RefreshCw,
  X,
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  FileDropZone,
  CompanyDiscoveryPreview,
  ConfidenceBadge,
  SectionToggle,
  ItemExclusionList,
  ImportPreviewDialog,
  AssumptionEditor,
  DiscoverySessionsList,
  ProcessingView,
  DiscoveryFlowPanel,
  type ExcludableItem,
  type ExistingDocument,
  type ModelId,
} from "@/components/Discovery";
import { ExtractionReviewPanel } from "@/components/Discovery/ExtractionReviewPanel";
import { trpc } from "@/lib/trpc";
import type { DiscoveryStatus, EnabledSections } from "@shared/discovery-types";
import { cn } from "@/lib/utils";
import { StepIndicator } from "@/components/ui/step-indicator";
import confetti from "canvas-confetti";
import { useFunnelTracking } from "@/hooks/useFunnelTracking";

type Step =
  | "name"
  | "upload"
  | "processing"
  | "preview"
  | "committing"
  | "done";

export default function CompanyDiscovery() {
  const [, navigate] = useLocation();
  const { trackEvent } = useFunnelTracking();
  const [step, setStep] = useState<Step>("name");
  const [projectName, setProjectName] = useState("");
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showReviewPanel, setShowReviewPanel] = useState(false);
  const [showImportSettings, setShowImportSettings] = useState(false);
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [processingFileCount, setProcessingFileCount] = useState(0);
  const [processingStartedAt, setProcessingStartedAt] = useState<number | null>(
    null
  );
  const [previewViewMode, setPreviewViewMode] = useState<"data" | "flow">(
    "data"
  );
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const [resumeSessionChecked, setResumeSessionChecked] = useState(false);
  const [industryTemplateSlug, setIndustryTemplateSlug] = useState("");

  const utils = trpc.useUtils();

  // === SESSION PERSISTENCE: Check for active session on mount ===
  const { data: activeSessionData } =
    trpc.companyDiscovery.getActiveSession.useQuery(undefined, {
      enabled: !resumeSessionChecked && sessionId === null,
      refetchOnWindowFocus: false,
    });

  // Show resume banner when an active session is found
  useEffect(() => {
    if (activeSessionData && !resumeSessionChecked) {
      setResumeSessionChecked(true);
      if (activeSessionData.session) {
        setShowResumeBanner(true);
      }
    }
  }, [activeSessionData, resumeSessionChecked]);

  // Resume the active session
  const handleResumeActiveSession = useCallback(() => {
    if (!activeSessionData?.session) return;
    const s = activeSessionData.session;
    setSessionId(s.id);
    setProjectName(s.projectName);
    setShowResumeBanner(false);
    setError(null);

    // Navigate to the last step, or determine from status
    const lastStep = s.lastStep as Step | null;
    if (lastStep && lastStep !== "name") {
      if (lastStep === "processing") {
        setStep("processing");
        setProcessingStartedAt(Date.now());
      } else {
        setStep(lastStep);
      }
    } else if (s.status === "ready") {
      setStep("preview");
    } else if (s.status === "processing") {
      setStep("processing");
      setProcessingStartedAt(Date.now());
    } else {
      setStep("upload");
    }
  }, [activeSessionData]);

  const handleDismissResumeBanner = useCallback(() => {
    setShowResumeBanner(false);
  }, []);

  // Save session state mutation
  const saveSessionState = trpc.companyDiscovery.saveSessionState.useMutation();

  // Clear session mutation
  const clearSessionMutation = trpc.companyDiscovery.clearSession.useMutation();

  // Helper: persist step transitions
  const persistStep = useCallback(
    (newStep: Step) => {
      if (sessionId) {
        saveSessionState.mutate({
          sessionId,
          lastStep: newStep,
        });
      }
    },
    [sessionId, saveSessionState]
  );

  // Mutations
  const startSession = trpc.companyDiscovery.startDiscoverySession.useMutation({
    onSuccess: data => {
      setSessionId(data.sessionId);
      setStep("upload");
      setError(null);
      // Persist the step transition
      saveSessionState.mutate({
        sessionId: data.sessionId,
        lastStep: "upload",
      });
    },
    onError: err => {
      setError(err.message);
    },
  });

  const processFiles = trpc.companyDiscovery.processDiscoveryFiles.useMutation({
    onSuccess: data => {
      setProcessingStartedAt(null);
      if (data.status === "ready") {
        trackEvent("extraction_complete", {
          sessionId: sessionId,
          processedCount: data.processedCount,
        });
        trackEvent("profile_review", { sessionId: sessionId });
        setStep("preview");
        persistStep("preview");
      } else if (data.status === "failed") {
        setError("Failed to process some files. Please try again.");
        setStep("upload");
        persistStep("upload");
      }
      utils.companyDiscovery.getDiscoveryPreview.invalidate();
    },
    onError: err => {
      setProcessingStartedAt(null);
      // Handle various error types with user-friendly messages
      let errorMessage = err.message;
      if (
        errorMessage.includes("Unexpected token") ||
        errorMessage.includes("<!DOCTYPE")
      ) {
        errorMessage =
          "Server is temporarily unavailable. The request may have timed out. Please try again with fewer files or wait a moment.";
      } else if (errorMessage.includes("Failed to fetch")) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      } else if (
        errorMessage.includes("timeout") ||
        errorMessage.includes("Timeout")
      ) {
        errorMessage = "Request timed out. Try uploading fewer files at once.";
      }
      setError(errorMessage);
      setStep("upload");
    },
  });

  const cancelProcessing = trpc.companyDiscovery.cancelProcessing.useMutation({
    onSuccess: () => {
      setStep("upload");
      setProcessingStartedAt(null);
    },
    onError: () => {
      // Even if server cancel fails, let the user go back
      setStep("upload");
      setProcessingStartedAt(null);
    },
  });

  const updateField = trpc.companyDiscovery.updateDiscoveryData.useMutation({
    onSuccess: () => {
      utils.companyDiscovery.getDiscoveryPreview.invalidate();
    },
  });

  const resolveConflict = trpc.companyDiscovery.resolveConflict.useMutation({
    onSuccess: () => {
      utils.companyDiscovery.getDiscoveryPreview.invalidate();
    },
  });

  // Unified onboarding controls
  const toggleSection = trpc.companyDiscovery.toggleSection.useMutation({
    onSuccess: () => {
      utils.companyDiscovery.getDiscoveryPreview.invalidate();
      utils.companyDiscovery.previewImport.invalidate();
    },
  });

  const toggleItem = trpc.companyDiscovery.toggleItem.useMutation({
    onSuccess: () => {
      utils.companyDiscovery.getDiscoveryPreview.invalidate();
      utils.companyDiscovery.previewImport.invalidate();
    },
  });

  const updateAssumption = trpc.companyDiscovery.updateAssumption.useMutation({
    onSuccess: () => {
      utils.companyDiscovery.getDiscoveryPreview.invalidate();
    },
  });

  const categorizeTransaction =
    trpc.companyDiscovery.categorizeTransaction.useMutation({
      onSuccess: () => {
        utils.companyDiscovery.getDiscoveryPreview.invalidate();
      },
    });

  const bulkCategorizeTransactions =
    trpc.companyDiscovery.bulkCategorizeTransactions.useMutation({
      onSuccess: () => {
        utils.companyDiscovery.getDiscoveryPreview.invalidate();
      },
    });

  const commitDiscovery = trpc.companyDiscovery.commitDiscovery.useMutation({
    onSuccess: data => {
      // Clear session state on successful commit
      if (sessionId) {
        clearSessionMutation.mutate({
          sessionId,
          action: "complete",
        });
      }
      // Celebrate!
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
      setStep("done");
      // Navigate to dashboard after a short delay - CompanyContext will auto-select the new company
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    },
    onError: err => {
      setError(err.message);
      setStep("preview");
    },
  });

  // Query for preview data (also used for existing documents in upload step)
  // Poll every 2 seconds when processing to stream progress updates
  const { data: previewData, isLoading: isLoadingPreview } =
    trpc.companyDiscovery.getDiscoveryPreview.useQuery(
      { sessionId: sessionId! },
      {
        enabled:
          sessionId !== null &&
          (step === "preview" || step === "processing" || step === "upload"),
        refetchInterval: step === "processing" ? 2000 : false, // Poll during processing
      }
    );

  // Query for import preview (what will be created)
  const { data: importPreviewData, isLoading: isLoadingImportPreview } =
    trpc.companyDiscovery.previewImport.useQuery(
      { sessionId: sessionId! },
      { enabled: sessionId !== null && step === "preview" && showImportPreview }
    );

  // Recovery: detect server completion via polling when mutation response was lost
  useEffect(() => {
    if (step !== "processing" || !previewData) return;

    if (previewData.session.status === "ready") {
      setStep("preview");
      setProcessingStartedAt(null);
      utils.companyDiscovery.getDiscoveryPreview.invalidate();
    } else if (previewData.session.status === "failed") {
      setError(
        previewData.session.error || "Processing failed. Please try again."
      );
      setStep("upload");
      setProcessingStartedAt(null);
    }
  }, [step, previewData?.session.status]);

  // Client-side timeout: move back to upload if stuck processing for over 5 minutes
  useEffect(() => {
    if (step !== "processing" || !processingStartedAt) return;

    const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
    const timer = setInterval(() => {
      if (Date.now() - processingStartedAt > TIMEOUT_MS) {
        setError(
          "Processing is taking longer than expected. Please try again with fewer files."
        );
        // Reset server-side session state so re-upload isn't blocked
        if (sessionId) {
          cancelProcessing.mutate({ sessionId });
        } else {
          setStep("upload");
          setProcessingStartedAt(null);
        }
      }
    }, 5000); // Check every 5s

    return () => clearInterval(timer);
  }, [step, processingStartedAt, sessionId, cancelProcessing]);

  // Get existing documents for FileDropZone
  const existingDocuments: ExistingDocument[] =
    previewData?.session.sourceDocuments?.map(doc => ({
      id: doc.id,
      name: doc.name,
      mimeType: doc.mimeType,
      size: doc.size,
      status:
        doc.processingStatus === "completed"
          ? "completed"
          : doc.processingStatus === "failed"
            ? "failed"
            : "pending",
      classification: doc.classification?.type?.replace(/_/g, " "),
      error: doc.error,
    })) || [];

  // Handlers
  const handleStartDiscovery = () => {
    if (!projectName.trim()) {
      setError("Please enter a project name");
      return;
    }
    startSession.mutate({ projectName: projectName.trim() });
  };

  // Resume an existing session
  const handleResumeSession = (
    id: number,
    name: string,
    status: DiscoveryStatus
  ) => {
    setSessionId(id);
    setProjectName(name);
    setError(null);

    // Navigate to appropriate step based on status
    if (status === "ready") {
      setStep("preview");
    } else if (status === "processing") {
      setStep("processing");
      setProcessingStartedAt(Date.now());
    } else {
      // pending, uploading, or failed - go to upload
      setStep("upload");
    }
  };

  const handleFilesSelected = useCallback(
    async (files: File[], modelId?: ModelId) => {
      if (!sessionId) return;

      // Track file upload funnel event
      trackEvent("file_upload", {
        fileCount: files.length,
        totalSizeBytes: files.reduce((sum, f) => sum + f.size, 0),
        sessionId: sessionId,
      });

      setStep("processing");
      setError(null);
      setProcessingFileCount(files.length);
      setProcessingStartedAt(Date.now());

      // Persist step and file metadata
      const fileMeta = files.map(f => ({
        name: f.name,
        size: f.size,
        mimeType: f.type,
        uploadedAt: Date.now(),
      }));
      saveSessionState.mutate({
        sessionId,
        lastStep: "processing",
        uploadedFilesMeta: fileMeta,
      });

      // Convert files to base64
      const processedFiles = await Promise.all(
        files.map(async file => {
          const buffer = await file.arrayBuffer();
          const base64 = btoa(
            new Uint8Array(buffer).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              ""
            )
          );
          return {
            name: file.name,
            content: base64,
            mimeType: file.type,
            size: file.size,
          };
        })
      );

      processFiles.mutate({
        sessionId,
        files: processedFiles,
        modelId: modelId !== "custom" ? modelId : undefined,
      });
    },
    [sessionId, processFiles, trackEvent]
  );

  const handleUpdateField = useCallback(
    (path: string, value: string) => {
      if (!sessionId) return;
      updateField.mutate({ sessionId, path, value });
    },
    [sessionId, updateField]
  );

  const handleResolveConflict = useCallback(
    (field: string, selectedIndex: number) => {
      if (!sessionId) return;
      resolveConflict.mutate({ sessionId, field, selectedIndex });
    },
    [sessionId, resolveConflict]
  );

  // Unified onboarding handlers
  const handleToggleSection = useCallback(
    (section: keyof EnabledSections, enabled: boolean) => {
      if (!sessionId) return;
      toggleSection.mutate({ sessionId, section, enabled });
    },
    [sessionId, toggleSection]
  );

  const handleToggleItem = useCallback(
    (
      itemType: "vendor" | "customer" | "bankAccount",
      itemId: string,
      excluded: boolean
    ) => {
      if (!sessionId) return;
      toggleItem.mutate({ sessionId, itemType, itemId, excluded });
    },
    [sessionId, toggleItem]
  );

  const handleUpdateAssumption = useCallback(
    (assumptionId: string, overrideValue: string | null) => {
      if (!sessionId) return;
      updateAssumption.mutate({
        sessionId,
        assumptionId,
        overrideValue: overrideValue ?? undefined,
      });
    },
    [sessionId, updateAssumption]
  );

  const handleCategorizeTransaction = useCallback(
    (transactionId: string, accountCode: string, accountName: string) => {
      if (!sessionId) return;
      categorizeTransaction.mutate({
        sessionId,
        transactionId,
        accountCode,
        accountName,
      });
    },
    [sessionId, categorizeTransaction]
  );

  const handleBulkCategorizeTransactions = useCallback(
    (transactionIds: string[], accountCode: string, accountName: string) => {
      if (!sessionId) return;
      bulkCategorizeTransactions.mutate({
        sessionId,
        transactionIds,
        accountCode,
        accountName,
      });
    },
    [sessionId, bulkCategorizeTransactions]
  );

  const handleCancelProcessing = useCallback(() => {
    if (sessionId) {
      cancelProcessing.mutate({ sessionId });
    } else {
      setStep("upload");
      setProcessingStartedAt(null);
    }
  }, [sessionId, cancelProcessing]);

  const handleShowImportPreview = () => {
    setShowImportPreview(true);
  };

  const handleConfirmCommit = () => {
    setShowImportPreview(false);
    handleCommit();
  };

  const handleCommit = () => {
    if (!sessionId) return;
    setStep("committing");
    persistStep("committing");
    const slug =
      industryTemplateSlug && industryTemplateSlug !== "__skip__"
        ? industryTemplateSlug
        : undefined;
    commitDiscovery.mutate({ sessionId, industryTemplateSlug: slug });
  };

  const handleBack = () => {
    switch (step) {
      case "upload":
        // Don't abandon the session - just go back to name step
        // The session is preserved and can be resumed
        setStep("name");
        persistStep("name");
        // Keep sessionId and projectName so user can see their draft in the list
        break;
      case "preview":
        // Go back to upload to add more documents
        // Keep session intact - documents are preserved
        setStep("upload");
        persistStep("upload");
        break;
      default:
        break;
    }
  };

  // Start fresh (clear current session context for new session)
  const handleStartFresh = () => {
    // Clear server-side session so it doesn't block future uploads
    if (sessionId) {
      clearSessionMutation.mutate({ sessionId, action: "delete" });
    }
    setSessionId(null);
    setProjectName("");
    setStep("name");
    // Allow getActiveSession to re-check on next mount
    setResumeSessionChecked(false);
  };

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case "name":
        return (
          <div className="max-w-lg mx-auto space-y-6">
            {/* Create New */}
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl">Create New Company</CardTitle>
                <CardDescription>
                  Start by giving your company project a name. You'll add
                  documents next.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label
                    htmlFor="projectName"
                    className="text-sm font-medium mb-2 block"
                  >
                    Company / Project Name
                  </label>
                  <Input
                    id="projectName"
                    placeholder="e.g., Acme GmbH or 2024 Tax Filing"
                    value={projectName}
                    onChange={e => setProjectName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleStartDiscovery()}
                    autoFocus
                  />
                </div>
                <Button
                  onClick={handleStartDiscovery}
                  disabled={!projectName.trim() || startSession.isPending}
                  className="w-full"
                  size="lg"
                >
                  {startSession.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      Start Discovery
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Existing Sessions / Drafts */}
            <DiscoverySessionsList onSelectSession={handleResumeSession} />
          </div>
        );

      case "upload":
        return (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Upload Documents</CardTitle>
                  <CardDescription>
                    {existingDocuments.length > 0
                      ? "Add more files or proceed to review your extracted data."
                      : "Drop your tax filings, bank statements, and financial documents. We'll extract company information automatically."}
                  </CardDescription>
                </div>
                {existingDocuments.length > 0 && (
                  <Button variant="outline" onClick={() => setStep("preview")}>
                    Skip to Review
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <FileDropZone
                onFilesSelected={handleFilesSelected}
                isProcessing={processFiles.isPending}
                existingDocuments={existingDocuments}
              />
            </CardContent>
          </Card>
        );

      case "processing":
        return (
          <ProcessingView
            fileCount={processingFileCount}
            processingProgress={previewData?.processingProgress}
            processingPhase={previewData?.session.processingPhase}
            onCancel={handleCancelProcessing}
          />
        );

      case "preview":
        if (isLoadingPreview || !previewData) {
          return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          );
        }

        return (
          <Tabs
            value={previewViewMode}
            onValueChange={v => setPreviewViewMode(v as "data" | "flow")}
            className={cn(
              "gap-6",
              previewViewMode === "flow"
                ? "max-w-6xl mx-auto"
                : "max-w-5xl mx-auto"
            )}
          >
            {/* Summary Header */}
            <Card>
              <CardContent className="py-4 space-y-4">
                {/* Row 1: Company name + confidence badge */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold">
                      {previewData.session.extractedData.companyInfo?.name ||
                        previewData.session.projectName}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {previewData.summary.companyFound
                        ? "Company information extracted"
                        : "Review and complete company information"}
                    </p>
                    <div className="flex gap-4 mt-2 text-sm">
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">
                          Bank Accounts:
                        </span>
                        <span className="font-medium">
                          {previewData.summary.bankAccountsCount}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">Vendors:</span>
                        <span className="font-medium">
                          {previewData.summary.vendorsCount}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">
                          Customers:
                        </span>
                        <span className="font-medium">
                          {previewData.summary.customersCount}
                        </span>
                      </div>
                      {previewData.summary.conflictsCount > 0 && (
                        <div className="flex items-center gap-1 text-warning">
                          <AlertTriangle className="h-4 w-4" />
                          <span>
                            {previewData.summary.conflictsCount} conflicts
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <ConfidenceBadge
                    confidence={previewData.summary.overallConfidence}
                    size="lg"
                  />
                </div>

                {/* Row 2: Tabs toggle + action buttons */}
                <div className="flex items-center justify-between gap-3 border-t pt-4">
                  <TabsList>
                    <TabsTrigger value="data" className="gap-1.5">
                      <List className="h-4 w-4" />
                      Data
                    </TabsTrigger>
                    <TabsTrigger value="flow" className="gap-1.5">
                      <LayoutPanelLeft className="h-4 w-4" />
                      Flow & Compliance
                    </TabsTrigger>
                  </TabsList>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowReviewPanel(true)}
                    >
                      <FileSearch className="h-4 w-4 mr-2" />
                      Review Extraction
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowImportSettings(true)}
                    >
                      <Settings2 className="h-4 w-4 mr-2" />
                      Import Settings
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* View Mode Content */}
            <TabsContent value="flow" className="mt-0">
              <DiscoveryFlowPanel
                documents={previewData.session.sourceDocuments || []}
                currentPhase={previewData.session.processingPhase}
                censusFirstResult={previewData.session.censusFirstResult}
                extractionMetadata={{
                  modelUsed: "Gemini 3.0 Flash",
                  modelVersion: "gemini-3-flash-preview",
                  region: "Zurich, Switzerland",
                  regionCode: "europe-west6",
                  datacenterLocation: "Google Cloud Zurich",
                  finmaCompliant: true,
                  dataResidency: "switzerland",
                  encryptionStatus: "both",
                }}
              />
            </TabsContent>
            <TabsContent value="data" className="mt-0">
              <CompanyDiscoveryPreview
                profile={previewData.session.extractedData}
                documents={previewData.session.sourceDocuments}
                conflicts={previewData.session.conflicts}
                onUpdateField={handleUpdateField}
                onResolveConflict={handleResolveConflict}
                onCategorizeTransaction={handleCategorizeTransaction}
                onBulkCategorizeTransactions={handleBulkCategorizeTransactions}
                sessionId={String(sessionId)}
                projectName={previewData.session.projectName}
                onRefineComplete={() =>
                  utils.companyDiscovery.getDiscoveryPreview.invalidate()
                }
                censusFirstResult={previewData.session.censusFirstResult}
                industryTemplateSlug={industryTemplateSlug}
                onIndustryTemplateChange={setIndustryTemplateSlug}
              />
            </TabsContent>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Add More Documents
              </Button>
              <Button onClick={handleShowImportPreview} size="lg">
                <Eye className="h-4 w-4 mr-2" />
                Preview & Create
              </Button>
            </div>

            {/* Extraction Review Panel */}
            <Sheet open={showReviewPanel} onOpenChange={setShowReviewPanel}>
              <SheetContent className="w-full sm:w-[600px] sm:max-w-[800px] overflow-auto">
                {sessionId && (
                  <ExtractionReviewPanel
                    sessionId={sessionId}
                    onClose={() => setShowReviewPanel(false)}
                  />
                )}
              </SheetContent>
            </Sheet>

            {/* Import Settings Panel */}
            <Sheet
              open={showImportSettings}
              onOpenChange={setShowImportSettings}
            >
              <SheetContent className="w-full sm:w-[500px] sm:max-w-[600px] overflow-auto">
                <SheetHeader>
                  <SheetTitle>Import Settings</SheetTitle>
                  <SheetDescription>
                    Choose which sections to import and exclude individual items
                  </SheetDescription>
                </SheetHeader>
                <div className="py-6 space-y-6">
                  {/* Section Toggles */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium">Sections to Import</h4>
                    <SectionToggle
                      label="Company Profile"
                      enabled={
                        previewData.session.enabledSections?.companyProfile ??
                        true
                      }
                      onToggle={enabled =>
                        handleToggleSection("companyProfile", enabled)
                      }
                      description="Name, UID, legal form, address"
                      disabled={toggleSection.isPending}
                    />
                    <SectionToggle
                      label="Bank Accounts"
                      enabled={
                        previewData.session.enabledSections?.bankAccounts ??
                        true
                      }
                      onToggle={enabled =>
                        handleToggleSection("bankAccounts", enabled)
                      }
                      count={previewData.summary.bankAccountsCount}
                      disabled={toggleSection.isPending}
                    />
                    <SectionToggle
                      label="Vendors"
                      enabled={
                        previewData.session.enabledSections?.vendors ?? true
                      }
                      onToggle={enabled =>
                        handleToggleSection("vendors", enabled)
                      }
                      count={previewData.summary.vendorsCount}
                      disabled={toggleSection.isPending}
                    />
                    <SectionToggle
                      label="Customers"
                      enabled={
                        previewData.session.enabledSections?.customers ?? true
                      }
                      onToggle={enabled =>
                        handleToggleSection("customers", enabled)
                      }
                      count={previewData.summary.customersCount}
                      disabled={toggleSection.isPending}
                    />
                  </div>

                  {/* Item Exclusion */}
                  {(previewData.session.enabledSections?.vendors ?? true) &&
                    previewData.session.extractedData.vendors.length > 0 && (
                      <div className="space-y-3">
                        <ItemExclusionList
                          title="Vendors to Import"
                          items={previewData.session.extractedData.vendors.map(
                            (v, i) =>
                              ({
                                id: v.normalizedName || `vendor-${i}`,
                                name: v.name,
                                subtitle: `${v.transactionCount} transaction${v.transactionCount !== 1 ? "s" : ""}`,
                                badge: v.category,
                                amount: -v.totalAmount, // Show as expense (negative)
                              }) as ExcludableItem
                          )}
                          excludedIds={
                            previewData.session.excludedItems?.vendorIds ?? []
                          }
                          onToggleItem={(itemId, excluded) =>
                            handleToggleItem("vendor", itemId, excluded)
                          }
                          disabled={toggleItem.isPending}
                        />
                      </div>
                    )}

                  {(previewData.session.enabledSections?.customers ?? true) &&
                    previewData.session.extractedData.customers.length > 0 && (
                      <div className="space-y-3">
                        <ItemExclusionList
                          title="Customers to Import"
                          items={previewData.session.extractedData.customers.map(
                            (c, i) =>
                              ({
                                id: c.normalizedName || `customer-${i}`,
                                name: c.name,
                                subtitle: `${c.transactionCount} transaction${c.transactionCount !== 1 ? "s" : ""}`,
                                badge:
                                  c.customerType !== "unknown"
                                    ? c.customerType
                                    : undefined,
                                amount: c.totalRevenue,
                              }) as ExcludableItem
                          )}
                          excludedIds={
                            previewData.session.excludedItems?.customerIds ?? []
                          }
                          onToggleItem={(itemId, excluded) =>
                            handleToggleItem("customer", itemId, excluded)
                          }
                          disabled={toggleItem.isPending}
                        />
                      </div>
                    )}

                  {/* Assumptions */}
                  {previewData.session.assumptions &&
                    previewData.session.assumptions.length > 0 && (
                      <AssumptionEditor
                        assumptions={previewData.session.assumptions}
                        onUpdateAssumption={handleUpdateAssumption}
                        disabled={updateAssumption.isPending}
                      />
                    )}
                </div>
              </SheetContent>
            </Sheet>

            {/* Import Preview Dialog */}
            <ImportPreviewDialog
              open={showImportPreview}
              onOpenChange={setShowImportPreview}
              preview={importPreviewData ?? null}
              isLoading={isLoadingImportPreview}
              onConfirm={handleConfirmCommit}
              isCommitting={commitDiscovery.isPending}
            />
          </Tabs>
        );

      case "committing":
        return (
          <Card className="max-w-lg mx-auto">
            <CardContent className="py-12 text-center">
              <Loader2 className="h-16 w-16 mx-auto mb-6 animate-spin text-primary" />
              <h3 className="text-xl font-semibold mb-2">Creating Company</h3>
              <p className="text-muted-foreground">
                Setting up your company, bank accounts, vendors, and
                customers...
              </p>
            </CardContent>
          </Card>
        );

      case "done":
        return (
          <Card className="max-w-lg mx-auto">
            <CardContent className="py-12 text-center">
              <div className="mx-auto mb-6 h-20 w-20 rounded-full bg-success/10 flex items-center justify-center">
                <Check className="h-10 w-10 text-success" />
              </div>
              <h3 className="text-2xl font-semibold mb-2">Company Created!</h3>
              <p className="text-muted-foreground mb-4">
                Your company has been set up successfully. Redirecting to
                dashboard...
              </p>
              <Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        );
    }
  };

  // Step indicator
  const discoverySteps = [
    { label: "Name" },
    { label: "Upload" },
    { label: "Review" },
    { label: "Done" },
  ];

  const stepKeyToIndex: Record<string, number> = {
    name: 0,
    upload: 1,
    processing: 1,
    preview: 2,
    committing: 2,
    done: 3,
  };

  const currentStepIndex = stepKeyToIndex[step] ?? 0;

  return (
    <DashboardLayout>
      <div className="container max-w-4xl py-8">
        {/* Step Indicator */}
        <StepIndicator steps={discoverySteps} currentStep={currentStepIndex} />

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Resume Banner */}
        {showResumeBanner && activeSessionData?.session && step === "name" && (
          <Alert className="mb-6 border-primary/30 bg-primary/5">
            <RefreshCw className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                You have an unfinished session:{" "}
                <strong>{activeSessionData.session.projectName}</strong>
                {activeSessionData.session.lastStep && (
                  <span className="text-muted-foreground ml-1">
                    (last step: {activeSessionData.session.lastStep})
                  </span>
                )}
              </span>
              <span className="flex items-center gap-2 ml-4 shrink-0">
                <Button
                  size="sm"
                  variant="default"
                  onClick={handleResumeActiveSession}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Resume
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDismissResumeBanner}
                >
                  <X className="h-3 w-3" />
                </Button>
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Back Button */}
        {step === "upload" && (
          <Button variant="ghost" onClick={handleBack} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}

        {/* Step Content */}
        {renderStepContent()}
      </div>
    </DashboardLayout>
  );
}
